import createPartLoader from './modelLoader';
import { Group, Vector3 } from 'three';
import { getContainerBox } from './utils/objectSize';

const PartNames = {
  wall_0: '0_simple_wall_element',
  wall_1: '1_special_wall_element',
  wall_2: '2_special_wall_element',
  wall_3: '3_special_wall_element',
  floor: 'floor_element',
  wall_top: 'simple_wall_top_element',
  light_wall_top: 'light_wall_top_element',
};

function moveToCenter(object) {
  const box = getContainerBox(object);
  const center = box.getCenter(new Vector3());
  object.translateZ(-center.z);
  object.translateX(-center.x);
}

export function createSurroundingsBuilder(loader) {
  const partsByType = {};

  async function update(options) {
    const { type } = options;
    if (!type) {
      return;
    }

    if (!partsByType[type]) {
      const partBasePath = `/3d/surroundings/${type}`;
      const partLoader = createPartLoader(loader);

      for (const key of Object.keys(PartNames)) {
        const name = PartNames[key];
        partLoader.add(key, `${partBasePath}/${name}/${name}.gltf`, (gltf) => {
          const scene = gltf.scene.clone();
          scene.rotateY(Math.PI * 0.5);
          return scene;
        });
      }

      partsByType[type] = partLoader.loadAll();
    }
    try {
      const parts = await partsByType[type];
      if (parts) {
        return build(parts, options);
      }
    } catch (e) {
      console.error('Error occurred while loading surroundings!', e);
    }
    return new Group();
  }

  function build(parts, options) {
    const { width = 20, length = 20, height = 3, containerBox } = options;

    const wallX = createWall(parts, width, height);
    wallX.position.z = -length * 0.5;

    const wallZ = createWall(parts, length, height);
    wallZ.rotation.y = Math.PI * 0.5;
    wallZ.position.x = -width * 0.5;

    const floor = createFloor(parts, width, length);

    const surroundings = new Group();
    surroundings.add(wallX, wallZ, floor);

    surroundings.position.y = containerBox.min.y;

    return surroundings;
  }

  const indexMap = [0, 1, 2, 3, 2, 1];
  function getWallIndex(i) {
    return indexMap[i % indexMap.length];
  }

  function createWall(parts, width, height) {
    const wall = new Group();
    const count = width / 5;
    let offset = -((count - 1) * 5) * 0.5;
    for (let i = 0; i < count; i++) {
      const wallSegment = new Group();
      wallSegment.position.x = offset;
      wall.add(wallSegment);
      offset += 5;
      wallSegment.add(parts[`wall_${getWallIndex(i)}`].clone());
      if (height > 3) {
        const topElement = parts.wall_top.clone();
        topElement.position.y = 3;
        wallSegment.add(topElement);
      }
      if (height > 4) {
        const topElement = parts.light_wall_top.clone();
        topElement.position.y = 4;
        wallSegment.add(topElement);
      }
    }
    return wall;
  }

  function createFloor(parts, width, length) {
    const floor = new Group();
    const countX = width / 5;
    const countY = length / 5;
    for (let x = 0; x < countX; x++) {
      for (let z = 0; z < countY; z++) {
        const floorElement = parts.floor.clone();
        floorElement.position.x = x * 5;
        floorElement.position.z = z * 5;
        floor.add(floorElement);
      }
    }
    moveToCenter(floor);
    return floor;
  }

  return {
    update,
  };
}
